import { Component, computed, inject, signal, ViewChild, viewChild } from '@angular/core';
import { DetailsDashboard } from '../../base-details-panel/details.dashboard';
import { DashboardLeerling, LeerlingDetail } from '../../Details';
import { combineLatest, map, Observable, ReplaySubject, switchMap } from 'rxjs';
import { Table } from '@cumlaude/metadata';
import { CustomExportItem, ExportOptions } from '../../../services/export.service';
import { FilterPanelComponent } from '../../../filter-panel/filter-panel.component';
import { FilterBarComponent } from '../../../filter-bar/filter-bar.component';
import { BasicFilterExpression, DataService, ExportDataOptions, ExportFilter } from '../../../services/data.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { AanwezigheidPerMaandComponent } from '../../../dashboards/aanwezigheid-statistieken/aanwezigheid-per-maand/aanwezigheid-per-maand.component';
import { AanwezigheidPerSchooldagComponent } from '../../../dashboards/aanwezigheid-statistieken/aanwezigheid-per-schooldag/aanwezigheid-per-schooldag.component';
import { AanwezigheidPerLesuurComponent } from '../../../dashboards/aanwezigheid-statistieken/aanwezigheid-per-lesuur/aanwezigheid-per-lesuur.component';
import { Dashboard } from '../../../shared/dashboard/base-dashboard/dashboard';
import { LegendaComponent } from '../../../legenda/legenda.component';
import { getAfgeslotenMaandenFilter } from '../../../services/schoolaanwezigheid';
import { FilterName } from '../../../services/filter-name';
import { AanwezigheidRedenenComponent } from '../../../dashboards/aanwezigheid-statistieken/aanwezigheid-redenen/aanwezigheid-redenen.component';

@Component({
	selector: 'app-leerling-aanwezigheid-details-panel',
	templateUrl: './leerling-aanwezigheid-details-panel.html',
	styleUrls: ['./leerling-aanwezigheid-details-panel.scss'],
	imports: [
		FilterPanelComponent,
		FilterBarComponent,
		AsyncPipe,
		AanwezigheidPerMaandComponent,
		AanwezigheidPerSchooldagComponent,
		AanwezigheidPerLesuurComponent,
		LegendaComponent,
		AanwezigheidRedenenComponent,
	],
})
export class LeerlingAanwezigheidDetailsPanelComponent extends Dashboard implements DetailsDashboard<LeerlingDetail> {
	private readonly dataService = inject(DataService);

	leerling$ = new ReplaySubject<DashboardLeerling>(1);
	readonly leerling = toSignal(this.leerling$);

	readonly aanwezigheidPerMaand = viewChild.required(AanwezigheidPerMaandComponent);

	readonly aanwezigheidRedenen = viewChild.required(AanwezigheidRedenenComponent);

	readonly aanwezigheidPerSchooldag = viewChild.required(AanwezigheidPerSchooldagComponent);

	readonly aanwezigheidPerLesuur = viewChild.required(AanwezigheidPerLesuurComponent);

	setSelected(selected: LeerlingDetail, schooljaar?: string) {
		this.leerling$.next({
			leerling: selected,
			schooljaarInfo: schooljaar ?? selected.lb_nm_schooljaar,
		});
	}

	doDetailsExport(exportOptions: ExportOptions) {
		this.doExport(this.filterExpressions(), this.permanentFilters(), exportOptions);
	}

	override getExportData(options: ExportDataOptions): Observable<Blob> {
		return this.dataService.getAanwezigheidExportData(options);
	}

	readonly factTable = Table.fac_aw_aanwezigheid;
	readonly defaultFilters: FilterName[] = ['aw_nm_schooljaar', 'aw_fun_nm_vak', 'aw_fun_nm_abs_reden'];

	readonly permanentFilters = computed(() => {
		const vestigingen = this.userService.schoolaanwezigheidVestigingen();
		return [
			new BasicFilterExpression(['aw_nr_leerling'], this.leerling()?.leerling.lb_nr_leerling),
			new BasicFilterExpression(['aw_is_presentieregistratie_verwerkt'], 1),
			getAfgeslotenMaandenFilter(),
			new BasicFilterExpression(['aw_id_vestiging'], vestigingen, 'in'),
		];
	});

	readonly schooljaarFilterOverride = computed(() => ({ aw_nm_schooljaar: this.leerling()?.schooljaarInfo }));

	override exportAsTable(exportOptions: ExportOptions, _title: string, filters: ExportFilter[]): Observable<Blob> {
		const exportPages = this.customExportItems
			.filter(({ condition }) => condition())
			.map(({ component }) => component().exportAsTable(exportOptions, filters));
		return combineLatest(exportPages).pipe(
			map((pages) => {
				return {
					showDatum: exportOptions.showDatum,
					pages,
				};
			}),
			switchMap((tableExport) => this.dataService.getTableExport(tableExport))
		);
	}

	readonly exportAanwezigheidPerMaand = signal(true);
	readonly exportAanwezigheidRedenen = signal(true);
	readonly exportAanwezigheidPerSchooldag = signal(true);
	readonly exportAanwezigheidPerLesuur = signal(true);

	customExportItems: CustomExportItem[] = [
		{ label: 'Aanwezigheid per maand', condition: this.exportAanwezigheidPerMaand, component: this.aanwezigheidPerMaand },
		{ label: 'Aanwezigheid redenen', condition: this.exportAanwezigheidRedenen, component: this.aanwezigheidRedenen },
		{ label: 'Gem. aanwezigheid per schooldag', condition: this.exportAanwezigheidPerSchooldag, component: this.aanwezigheidPerSchooldag },
		{ label: 'Gem. aanwezigheid per lesuur', condition: this.exportAanwezigheidPerLesuur, component: this.aanwezigheidPerLesuur },
	];

	@ViewChild(LegendaComponent)
	legendaComponent?: LegendaComponent;
}
